import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import { formatDate } from "@/lib/utils"
import { Head, usePage } from "@inertiajs/react"
import { IconArrowLeft, IconExternalLink, IconPencil } from "@tabler/icons-react"
import { STATUS_BADGE } from "./status"

interface RelatedDocument {
  id: number
  title: string
  document_type_label: string
  status?: string
  effective_date?: string | null
}

interface LegalDocument {
  id: number
  title: string
  document_type: string
  document_type_label: string
  status: string
  customer: { id: number; name: string }
  parent_document: RelatedDocument | null
  child_documents: RelatedDocument[]
  effective_date: string | null
  execution_date: string | null
  end_date: string | null
  governing_law: string | null
  currency: string | null
  document_url: string | null
  notes: string | null
  can_update: boolean
}

interface Props {
  [key: string]: unknown
  document: LegalDocument
}

export default function LegalDocumentShow() {
  const { document } = usePage<Props>().props
  const badge = STATUS_BADGE[document.status] || { variant: "outline" as const, label: document.status }

  return (
    <>
      <Head title={`${document.title} - Operations Center`} />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/legal_documents">Documents</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{document.title}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                <a href="/legal_documents">
                  <IconArrowLeft className="h-4 w-4" />
                </a>
              </Button>
              <div>
                <div className="flex items-center gap-2.5">
                  <h1 className="text-lg font-semibold tracking-tight">{document.title}</h1>
                  <Badge variant="secondary">{document.document_type_label}</Badge>
                  <Badge variant={badge.variant}>{badge.label}</Badge>
                </div>
                <p className="text-muted-foreground text-sm">
                  <a href={`/customers/${document.customer.id}`} className="hover:underline">
                    {document.customer.name}
                  </a>
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              {document.document_url && (
                <Button size="sm" variant="outline" asChild>
                  <a href={document.document_url} target="_blank" rel="noopener noreferrer">
                    <IconExternalLink className="mr-1.5 h-4 w-4" />
                    Open
                  </a>
                </Button>
              )}
              {document.can_update && (
                <Button size="sm" asChild>
                  <a href={`/legal_documents/${document.id}/edit`}>
                    <IconPencil className="mr-1.5 h-4 w-4" />
                    Edit
                  </a>
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <div className="space-y-6 xl:col-span-2">
            <div className="rounded-xl border bg-card p-5">
              <h3 className="mb-3 text-sm font-medium">Dates</h3>
              <dl className="space-y-2.5 text-sm">
                <MetaRow label="Effective" value={formatDate(document.effective_date)} />
                <MetaRow label="Executed" value={formatDate(document.execution_date)} />
                <MetaRow label="Ends" value={formatDate(document.end_date)} />
              </dl>
            </div>

            {document.child_documents.length > 0 && (
              <div className="rounded-xl border bg-card">
                <div className="p-5 pb-0">
                  <h3 className="text-sm font-medium">Related documents ({document.child_documents.length})</h3>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Effective</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {document.child_documents.map((child) => {
                        const childBadge = STATUS_BADGE[child.status || ""] || {
                          variant: "outline" as const,
                          label: child.status || "",
                        }

                        return (
                          <TableRow key={child.id}>
                            <TableCell>
                              <a
                                href={`/legal_documents/${child.id}`}
                                className="text-primary font-medium hover:underline"
                              >
                                {child.title}
                              </a>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary">{child.document_type_label}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant={childBadge.variant}>{childBadge.label}</Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm tabular-nums">
                              {formatDate(child.effective_date ?? null)}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {document.notes && (
              <div className="rounded-xl border bg-card p-5">
                <h3 className="mb-3 text-sm font-medium">Notes</h3>
                <p className="text-sm whitespace-pre-line">{document.notes}</p>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="rounded-xl border bg-card p-5">
              <h3 className="mb-3 text-sm font-medium">Details</h3>
              <dl className="space-y-2.5 text-sm">
                <MetaRow label="Type" value={document.document_type_label} />
                {document.governing_law && <MetaRow label="Governing law" value={document.governing_law} />}
                {document.currency && <MetaRow label="Currency" value={document.currency.toUpperCase()} />}
                {document.parent_document && (
                  <>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <dt className="text-muted-foreground">Under</dt>
                      <dd>
                        <a
                          href={`/legal_documents/${document.parent_document.id}`}
                          className="text-primary font-medium hover:underline"
                        >
                          {document.parent_document.title}
                        </a>
                      </dd>
                    </div>
                  </>
                )}
              </dl>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  )
}

LegalDocumentShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
